import { useState, type ReactElement } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Stepper } from '@/components/ui/stepper';

import type { DeleteImpactDto, DeleteTargetType, MembersMode } from './api';
import { DeleteImpactSummary } from './DeleteImpactSummary';

interface DeleteWizardDialogProps {
  readonly targetType: DeleteTargetType;
  /** Human-readable name of the target, echoed in the copy and used as the type-to-confirm phrase. */
  readonly entityLabel: string;
  readonly impact: DeleteImpactDto;
  /** Family f1/f2 branch — owned by the parent because flipping it re-fetches the preview. */
  readonly membersMode: MembersMode;
  readonly onMembersModeChange: (next: MembersMode) => void;
  /** True while a preview re-fetch is in flight (after an f1/f2 flip). */
  readonly isPreviewFetching: boolean;
  /** True while the delete job is being launched. */
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

/**
 * Multi-step delete confirmation (the `FamilyCreateWizard` shape) driven by a {@link DeleteImpactDto}.
 *
 * Step 1 previews the cascade (DELETE / UNLINK counts) and, for a FAMILY target, offers the f1
 * ("unlink members", default) vs f2 ("delete all members") choice — flipping it re-fetches the
 * preview so the counts always reflect the selected branch. Step 2 confirms; when the backend flags
 * `requires_confirmation` (a large / multi-entity cascade) the confirm button stays disabled until the
 * operator types the exact entity name, the same friction gate as `WipeTenantConfirmDialog`.
 */
export function DeleteWizardDialog({
  targetType,
  entityLabel,
  impact,
  membersMode,
  onMembersModeChange,
  isPreviewFetching,
  busy,
  onCancel,
  onConfirm,
}: DeleteWizardDialogProps): ReactElement {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState('');

  const stepLabels = [
    t('delete.wizard.step.review', { defaultValue: 'Review impact' }),
    t('delete.wizard.step.confirm', { defaultValue: 'Confirm' }),
  ];

  const typeToConfirm = impact.requires_confirmation;
  // Match the displayed confirm phrase robustly: trim BOTH sides and compare case-insensitively, so a
  // trailing space or a capitalisation difference in the record's name can't leave the button stuck
  // disabled after the operator has typed the name they were shown.
  const gateSatisfied =
    !typeToConfirm || typed.trim().toLocaleLowerCase() === entityLabel.trim().toLocaleLowerCase();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" aria-hidden="true" />
            {t('delete.wizard.title', { defaultValue: 'Delete {{name}}', name: entityLabel })}
          </DialogTitle>
          <DialogDescription>
            {t('delete.wizard.subtitle', {
              defaultValue: 'Review what this removes before confirming. This cannot be undone.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-1">
          <Stepper
            steps={stepLabels}
            currentStep={step}
            aria-label={t('delete.wizard.stepperAria', { defaultValue: 'Delete steps' })}
          />
        </div>

        {step === 0 && (
          <div className="space-y-4">
            {targetType === 'FAMILY' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-foreground">
                  {t('delete.wizard.members.legend', { defaultValue: 'What happens to the members?' })}
                </legend>
                <div className="flex items-start gap-2 rounded-lg border border-border p-2.5 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    id="delete-members-unlink"
                    type="radio"
                    name="delete-members-mode"
                    className="mt-0.5"
                    checked={membersMode === 'UNLINK'}
                    onChange={() => {
                      onMembersModeChange('UNLINK');
                    }}
                  />
                  <label htmlFor="delete-members-unlink" className="cursor-pointer">
                    <span className="font-medium">
                      {t('delete.wizard.members.unlink', { defaultValue: 'Unlink members (recommended)' })}
                    </span>
                    <span className="block text-muted-foreground">
                      {t('delete.wizard.members.unlinkHint', {
                        defaultValue: 'Delete the family only. Member records are kept.',
                      })}
                    </span>
                  </label>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-border p-2.5 text-sm has-[:checked]:border-destructive has-[:checked]:bg-destructive/5">
                  <input
                    id="delete-members-delete"
                    type="radio"
                    name="delete-members-mode"
                    className="mt-0.5"
                    checked={membersMode === 'DELETE'}
                    onChange={() => {
                      onMembersModeChange('DELETE');
                    }}
                  />
                  <label htmlFor="delete-members-delete" className="cursor-pointer">
                    <span className="font-medium text-destructive">
                      {t('delete.wizard.members.delete', { defaultValue: 'Delete all members' })}
                    </span>
                    <span className="block text-muted-foreground">
                      {t('delete.wizard.members.deleteHint', {
                        defaultValue: 'Also delete every member of this family. Bounded — stops at one layer.',
                      })}
                    </span>
                  </label>
                </div>
              </fieldset>
            )}

            <div aria-busy={isPreviewFetching} className={isPreviewFetching ? 'opacity-60' : undefined}>
              <DeleteImpactSummary impact={impact} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
              <p className="text-sm text-destructive">
                {t('delete.wizard.finalWarning', {
                  defaultValue: 'This permanently deletes the items shown in the previous step and cannot be undone.',
                })}
              </p>
            </div>
            {typeToConfirm && (
              <div className="space-y-1.5">
                <label htmlFor="delete-confirm-input" className="block text-sm">
                  {t('delete.wizard.typeToConfirm', {
                    defaultValue: 'Type {{name}} to confirm.',
                    name: entityLabel,
                  })}
                </label>
                <Input
                  id="delete-confirm-input"
                  value={typed}
                  onChange={(e) => {
                    setTyped(e.target.value);
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono"
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 0 ? (
            <>
              <Button variant="ghost" onClick={onCancel} disabled={busy}>
                {t('ui.dialog.close', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="destructive"
                disabled={isPreviewFetching}
                onClick={() => {
                  setStep(1);
                }}
              >
                {isPreviewFetching && (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {t('delete.wizard.nav.next', { defaultValue: 'Continue' })}
                <ArrowRight className="ms-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStep(0);
                }}
                disabled={busy}
              >
                <ArrowLeft className="me-2 h-4 w-4" aria-hidden="true" />
                {t('delete.wizard.nav.back', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={!gateSatisfied}
                loading={busy}
                loadingText={t('delete.wizard.nav.deleting', { defaultValue: 'Deleting…' })}
              >
                <Trash2 className="me-2 h-4 w-4" aria-hidden="true" />
                {t('delete.wizard.nav.delete', { defaultValue: 'Delete' })}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
